import { AxiosError, isAxiosError } from 'axios'
import toastService from './toastService'

type BackendError = { error?: string; detail?: string; message?: string }

// Marks an error whose toast was already shown by the global axios interceptor,
// so a local catch handler doesn't stack a second toast on top of it.
const TOAST_SHOWN = Symbol('toastShown')

// getApiErrorMessage pulls the human-readable reason out of a failed request.
// The Go backend returns it under `error` (gin); `detail` / `message` are kept
// as fallbacks for any other shape.
export function getApiErrorMessage(error: unknown): string | undefined {
  if (isAxiosError(error)) {
    const data = (error as AxiosError<BackendError>).response?.data
    return data?.error || data?.detail || data?.message || undefined
  }
  return undefined
}

export function markToastShown(error: unknown): void {
  if (error && typeof error === 'object') {
    ;(error as Record<symbol, unknown>)[TOAST_SHOWN] = true
  }
}

function wasToastShown(error: unknown): boolean {
  return !!(error && typeof error === 'object' && (error as Record<symbol, unknown>)[TOAST_SHOWN])
}

// toastApiError shows a single error toast for a failed API call: the backend
// message when present, otherwise the provided fallback. It no-ops when the
// global interceptor already toasted this error (401/403/5xx), so callers can
// use it unconditionally without producing double toasts.
export function toastApiError(error: unknown, fallback: string): void {
  if (wasToastShown(error)) return
  toastService.error(getApiErrorMessage(error) || fallback)
}
